import { startTransition, useEffect, useLayoutEffect, useState } from "react";

import L from "leaflet";
import ReactDOM from "react-dom";
import { useMap } from "react-leaflet";

export type LeafletControlWrapperProps = {
  children: React.ReactNode;
  position: L.ControlPosition;
  useDefaultStyle?: boolean;
};

/**
 * Use as a wrapper for a react element to be placed in a leaflet-control div
 * using the native leaflet positioning.
 */
export const LeafletControlWrapper = ({
  children,
  position,
  useDefaultStyle = false,
}: LeafletControlWrapperProps) => {
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null);
  const map = useMap();

  // A layout effect, so the controls are added to the map's corners in their order in the tree. The wrapper is set as a
  // transition: a state update while React commits would otherwise make it rerender the controls synchronously, and run
  // the effects of the whole commit with it, in one long task. As a transition the content is rendered in short slices.
  useLayoutEffect(() => {
    const control = new L.Control({ position });

    control.onAdd = () => {
      const classNames = useDefaultStyle ? "leaflet-bar" : "";
      const div = L.DomUtil.create("div", `${classNames} custom-control`);
      startTransition(() => setWrapper(div));
      return div;
    };
    control.addTo(map);

    return () => {
      control.remove();
    };
  }, [map, position, useDefaultStyle]);

  useEffect(() => {
    if (wrapper) {
      L.DomEvent.disableClickPropagation(wrapper);
    }
  }, [wrapper]);

  return wrapper ? ReactDOM.createPortal(children, wrapper) : null;
};
